import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import useCompanySettings from '../hooks/useCompanySettings';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

function SalaryCalculator() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [baseSalary, setBaseSalary] = useState(null);
  const [overtimeRate, setOvertimeRate] = useState(null);
  const [overtimeHours, setOvertimeHours] = useState('');
  const [totalSalary, setTotalSalary] = useState(null);
  const [message, setMessage] = useState('');
  const settings = useCompanySettings();

  useEffect(() => {
    if (settings && settings.overtimeRate) {
      setOvertimeRate(parseFloat(settings.overtimeRate));
    }
    if (settings && settings.currency) {
      // currency kullanılacaksa burada kullanılabilir
    }
  }, [settings]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('https://pdks.bytebridge.com.tr/api/employee');
        setEmployees(response.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setMessage('Çalışanlar alınamadı.');
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (employeeId) {
      fetchOvertimeHoursData();
    }
  }, [employeeId]);

  const fetchBaseSalary = async () => {
    try {
      const response = await axios.get(`https://pdks.bytebridge.com.tr/api/employee/${employeeId}/base-salary`);
      setBaseSalary(response.data.base_salary);
      if (!overtimeRate) {
        const hourlyRate = response.data.base_salary / 160;
        setOvertimeRate(hourlyRate * 1.5);
      }
    } catch (error) {
      console.error('Error fetching base salary:', error);
      setMessage('Çalışan bulunamadı veya temel maaş alınamadı.');
      setBaseSalary(null);
      setOvertimeRate(null);
    }
  };

  // Yardımcı fonksiyon: Alan adını esnek bul
  function getField(obj, ...fields) {
    for (const f of fields) {
      if (obj && Object.prototype.hasOwnProperty.call(obj, f)) return obj[f];
      // snake_case/kebab-case/camelCase farkı için normalize et
      const keys = Object.keys(obj || {});
      const found = keys.find(k => k.toLowerCase() === f.toLowerCase());
      if (found) return obj[found];
    }
    return undefined;
  }

  // Yardımcı fonksiyon: Ay formatlarını normalize et
  function normalizeMonth(val) {
    if (!val) return '';
    // 2025-4 veya 2025-04 -> 2025-04
    const [y, m] = val.split('-');
    if (!y || !m) return val;
    return `${y}-${m.padStart(2, '0')}`;
  }

  const fetchOvertimeHoursData = async () => {
    try {
      const response = await axios.get(`https://pdks.bytebridge.com.tr/api/attendance/${employeeId}`);
      const currentDate = new Date();
      const currentMonth = currentDate.toISOString().slice(0, 7); // YYYY-MM
      // Alan adlarını esnek kontrol et ve ay formatını normalize et
      const currentMonthOvertime = (response.data || []).find(item => {
        const monthVal = normalizeMonth(getField(item, 'Month', 'month'));
        return monthVal === currentMonth;
      });
      const overtimeVal = getField(currentMonthOvertime || {}, 'OvertimeHours', 'overtime_hours', 'overtime');
      if (currentMonthOvertime && overtimeVal > 0) {
        setOvertimeHours(overtimeVal.toString());
        setMessage(`Bu ay için ${overtimeVal} saat fazla mesai kaydı bulundu.`);
      } else {
        setOvertimeHours('0');
        setMessage('Bu ay için fazla mesai kaydı bulunamadı.');
      }
    } catch (error) {
      console.error('Error fetching overtime data:', error);
      setMessage('Fazla mesai saatleri alınamadı. Lütfen manuel olarak girin.');
      setOvertimeHours('0');
    }
  };

  const handleCalculateSalary = async () => {
    // Validate all required fields
    if (!employeeId) {
      setMessage('Lütfen bir çalışan seçin.');
      return;
    }
    if (baseSalary === null || baseSalary === undefined) {
      setMessage('Lütfen önce temel maaşı alın.');
      return;
    }
    if (overtimeRate === null || overtimeRate === undefined) {
      setMessage('Fazla mesai ücreti hesaplanamadı.');
      return;
    }
    // Mesai saati boş, null, 0 veya geçersiz ise temel maaşı döndür
    if (!overtimeHours || isNaN(parseFloat(overtimeHours)) || parseFloat(overtimeHours) <= 0) {
      setTotalSalary(parseFloat(baseSalary));
      setMessage('Bu ay için fazla mesai yok. Temel maaş gösterildi.');
      return;
    }
    const overtimeHoursNum = parseFloat(overtimeHours);
    if (overtimeHoursNum < 0) {
      setMessage('Fazla mesai saati negatif olamaz.');
      return;
    }
    try {
      const payload = {
        employee_id: employeeId,
        base_salary: parseFloat(baseSalary),
        overtime_rate: parseFloat(overtimeRate),
        overtime_hours: overtimeHoursNum
      };
      // Validate all values are numbers and not NaN
      if (Object.values(payload).some(val => isNaN(val))) {
        setMessage('Geçersiz sayısal değerler var.');
        return;
      }
      const response = await axios.post('https://pdks.bytebridge.com.tr/api/calculate-salary', payload);
      setTotalSalary(response.data.total_salary);
      setMessage(`Maaş başarıyla hesaplandı. Fazla mesai ücreti: ${(overtimeHoursNum * parseFloat(overtimeRate)).toFixed(2)}`);
    } catch (error) {
      console.error('Error calculating salary:', error);
      setMessage(error.response?.data?.message || 'Maaş hesaplanırken bir hata oluştu.');
      setTotalSalary(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const empRes = await axios.get('https://pdks.bytebridge.com.tr/api/employees');
      const employees = empRes.data;
      console.log("EMPLOYEES RAW DATA", employees);
      const currentDate = new Date();
      const currentMonth = currentDate.toISOString().slice(0, 7);
      const excelRows = [
        ['Personel ID', 'Ad Soyad', 'Departman', 'Temel Maaş', 'Fazla Mesai Saati', 'Fazla Mesai Ücreti', 'Toplam Maaş']
      ];

      for (const emp of employees) {
        console.log("EMPLOYEE OBJ:", emp);
        // Doğrudan örnek objeye göre property'leri çek
        const employeeId = emp.EmployeeID || emp.ID || '';
        const firstName = emp.FirstName || '';
        const lastName = emp.LastName || '';
        const employeeName = (firstName + ' ' + lastName).trim();
        const department = emp.Department || '';
        const baseSalaryRaw = emp.BaseSalary ?? 0;
        const baseSalary = typeof baseSalaryRaw === 'string' ? Number(baseSalaryRaw.replace(',', '.')) : Number(baseSalaryRaw) || 0;
        const overtimeRate = settings && settings.overtimeRate ? parseFloat(settings.overtimeRate) : (baseSalary / 160) * 1.5;

        if (!employeeId) {
          console.warn('Boş employeeId ile istek atlanıyor!', emp);
          continue;
        }

        // Fazla mesai saatini çek
        let overtimeHours = 0;
        try {
          const attRes = await axios.get(`https://pdks.bytebridge.com.tr/api/attendance/${employeeId}`);
          const attendanceData = attRes.data || [];
          const monthData = attendanceData.find(item => {
            const monthVal = normalizeMonth(getField(item, 'Month', 'month'));
            return monthVal === currentMonth;
          });
          overtimeHours = getField(monthData || {}, 'OvertimeHours', 'overtime_hours', 'overtime') || 0;
        } catch (err) {
          overtimeHours = 0;
        }

        // Toplam maaş hesaplama
        let totalSalary = baseSalary;
        let overtimeAmount = 0;
        if (overtimeHours > 0) {
          overtimeAmount = Number(overtimeHours) * Number(overtimeRate);
          totalSalary += overtimeAmount;
        }

        console.log({
          employeeId,
          employeeName,
          department,
          baseSalaryRaw,
          baseSalary
        });

        excelRows.push([
          employeeId,
          employeeName,
          department,
          baseSalary > 0 ? baseSalary : 'Eksik',
          overtimeHours,
          overtimeRate.toFixed(2),
          totalSalary.toFixed(2)
        ]);
      }

      // Excel oluştur
      const ws = XLSX.utils.aoa_to_sheet(excelRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Maaşlar');
      XLSX.writeFile(wb, `maaslar_${currentMonth}.xlsx`);
    } catch (err) {
      alert('Excel aktarma sırasında hata oluştu!');
    }
  };

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <Grid item xs={12}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <Tooltip title="Bu sayfada maaş bilgilerini görüntüleyebilirsiniz.">
            <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
          </Tooltip>
          <Typography variant="h4" gutterBottom>
            MAAŞ HESAPLAMA
          </Typography>
        </div>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="employee-id-label">Çalışan Seçin</InputLabel>
                <Select
                  labelId="employee-id-label"
                  id="employee-id"
                  value={employeeId}
                  label="Çalışan Seçin"
                  onChange={(e) => setEmployeeId(e.target.value)}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.employee_id} value={employee.employee_id}>
                      {employee.employee_name} ({employee.employee_id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={fetchBaseSalary}>
                Temel Maaşı Al
              </Button>
              <Button variant="outlined" color="success" onClick={handleExportExcel}>
                Tüm Maaşları Excel'e Aktar
              </Button>
            </Grid>
            {baseSalary !== null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Temel Maaş"
                  variant="outlined"
                  value={baseSalary}
                  fullWidth
                  disabled
                />
              </Grid>
            )}
            {overtimeRate !== null && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fazla Mesai Ücreti"
                  variant="outlined"
                  value={overtimeRate}
                  fullWidth
                  disabled
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fazla Mesai Saati"
                variant="outlined"
                value={overtimeHours}
                onChange={(e) => setOvertimeHours(e.target.value)}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleCalculateSalary}>
                Maaş Hesapla
              </Button>
            </Grid>
            {message && (
              <Grid item xs={12}>
                <Typography variant="body1" color={totalSalary !== null ? 'success' : 'error'}>
                  {message}
                </Typography>
              </Grid>
            )}
            {totalSalary !== null && (
              <Grid item xs={12}>
                <Typography variant="h6">Toplam Maaş: {totalSalary}</Typography>
              </Grid>
            )}
            {/* EXCEL/CSV AKTAR BUTONU */}
            {/* <Grid item xs={12}>
              <Button variant="outlined" color="success" onClick={handleExportExcel}>
                Excel'e Aktar (Tüm Personel Aylık Maaş)
              </Button>
            </Grid> */}
          </Grid>
        </Paper>
      </Grid>
    </Box>
  );
}

export default SalaryCalculator;